import { type FC, useEffect, useMemo, useState } from "react";

import {
  Alert,
  ArrowRightIcon,
  Box,
  Column,
  Combobox,
  ErrorIcon,
  GroupedCombobox,
  HiddenFormLabel,
  type IconAccessory,
  Row,
  SparkleIcon,
  TextInput,
  Tooltip,
  TraitIcon,
} from "@hightouch/htui";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import isEqual from "lodash/isEqual";
import {
  type ExtendedOption,
  isExtendedOption,
  type Option,
} from "@hightouch/formkit";

import { useDestinationForm } from "src/contexts/destination-form-context";
import { usePermissionContext } from "src/components/permission/permission-context";
import { convertType, NEW_ICON_MAP } from "src/utils/destinations";
import { suggest } from "src/utils/automap";
import {
  type ColumnOption,
  useFormkitContext,
  useFormkitDataContext,
} from "src/formkit/components/formkit-context";
import { ColumnType } from "@hightouch/lib/query/visual/types";
import {
  type JsonColumnProps,
  MappingType,
} from "src/formkit/components/types";
import { getExpectedColumnOptions } from "src/events/utils/analyzed-event-schemas";
import {
  isTraitReference,
  resolveFromValue,
  resolveSourceColumn,
  resolveTraitReference,
} from "src/formkit/components/utils";
import { Link } from "src/router";
import { Mapper } from "./mapper";
import type { GetIsValidProps } from "./types";
import { MappingsHeader } from "./mappings-header";
import { TypeSelect } from "./type-select";
import { MAPPING_COLUMN_GAP, MAPPING_ROW_COLUMNS } from "./utils";

import { isSyncMatchBoosted } from "src/pages/syncs/sync/matchbooster";

/**
 * Frontend-local copies of backend constants to avoid importing
 * @hightouch/lib (which pulls in Node-only transitive deps).
 * Keep in sync with packages/backend/lib/query/array-expansion/index.ts.
 */
const ARRAY_EXPANSION_SOURCE_COLUMN_CONFIG_KEY = "arrayExpansionSourceColumn";
function getArrayExpansionElementColumn(sourceColumn: string): string {
  return `_ht_${sourceColumn}_element`;
}

const ARRAY_EXPANDABLE_COLUMN_TYPES: ReadonlySet<string> = new Set([
  ColumnType.JsonArrayStrings,
  ColumnType.JsonArrayNumbers,
]);

function isArrayLikeColumnType(type: string | null | undefined): boolean {
  if (!type) return false;
  return ARRAY_EXPANDABLE_COLUMN_TYPES.has(type);
}

const ArrayExpansionSection: FC<{
  flatColumns: ColumnOption[];
  mappingName: string;
}> = ({ flatColumns, mappingName }) => {
  const { setValue } = useFormContext();
  const mappingFrom = useWatch({ name: `${mappingName}.from` });
  const arrayExpansionValue = useWatch({
    name: ARRAY_EXPANSION_SOURCE_COLUMN_CONFIG_KEY,
  });
  const formValues = useWatch() as unknown as Record<string, unknown>;

  // A `from` can be a raw column string or an audience-trait reference object.
  // resolveSourceColumn handles the string case (and is string-only by design);
  // resolveTraitReference resolves a trait object to its column name so the
  // array-column auto-detect below fires for array-typed traits too.
  const resolvedSourceColumn =
    resolveSourceColumn(mappingFrom) ?? resolveTraitReference(mappingFrom);

  const isArrayColumn = useMemo(() => {
    if (!resolvedSourceColumn) return false;
    return flatColumns.some((col) => {
      if (!isArrayLikeColumnType(col.type)) return false;
      const colName = typeof col.value === "string" ? col.value : col.label;
      return colName === resolvedSourceColumn;
    });
  }, [flatColumns, resolvedSourceColumn]);

  const isMatchBoosted = isSyncMatchBoosted(formValues);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (!resolvedSourceColumn || !isArrayColumn || isMatchBoosted) {
      if (arrayExpansionValue) {
        setValue(ARRAY_EXPANSION_SOURCE_COLUMN_CONFIG_KEY, undefined, {
          shouldDirty: true,
        });
      }
      return;
    }

    // Trait reference (a recognized trait object, not a raw column name): store
    // the reference verbatim. `mappingFrom` already points at the trait, so the
    // destination's identity mapping and `arrayExpansionSourceColumn` resolve
    // to the same alias on the backend, which then points the identity
    // mapping's `from` at the derived `_ht_<alias>_element` column.
    if (isTraitReference(mappingFrom)) {
      if (!isEqual(arrayExpansionValue, mappingFrom)) {
        setValue(ARRAY_EXPANSION_SOURCE_COLUMN_CONFIG_KEY, mappingFrom, {
          shouldDirty: true,
        });
      }
      return;
    }

    if (arrayExpansionValue !== resolvedSourceColumn) {
      setValue(ARRAY_EXPANSION_SOURCE_COLUMN_CONFIG_KEY, resolvedSourceColumn, {
        shouldDirty: true,
      });
    }

    const elementColumn = getArrayExpansionElementColumn(resolvedSourceColumn);
    if (mappingFrom !== elementColumn) {
      setValue(`${mappingName}.from`, elementColumn, { shouldDirty: true });
    }
  }, [
    resolvedSourceColumn,
    isArrayColumn,
    isMatchBoosted,
    mappingFrom,
    mappingName,
    arrayExpansionValue,
  ]);

  if (!isArrayColumn || !resolvedSourceColumn) return null;

  if (isMatchBoosted) {
    return (
      <Alert
        variant="inline"
        type="warning"
        title="Array expansion unavailable"
        message={
          <>
            Array expansion is not compatible with Match Booster. The array
            column will not be expanded.{" "}
            <Link
              href="https://docs.hightouch.com/syncs/array-expansion"
              style={{ textDecoration: "underline" }}
            >
              Learn more
            </Link>
          </>
        }
        mt={2}
      />
    );
  }

  return (
    <Alert
      variant="inline"
      type="info"
      title="Array expansion enabled"
      message={
        <>
          Array expansion is enabled on &ldquo;{resolvedSourceColumn}&rdquo;.{" "}
          <Link
            href="https://docs.hightouch.com/syncs/array-expansion"
            style={{ textDecoration: "underline" }}
          >
            Learn more
          </Link>
        </>
      }
      mt={2}
    />
  );
};

type Props = {
  name: string;
  options?: ExtendedOption[];
  /**
  @default Columns in the model. Use ExtendedOptions for overriding that from model from destination.
  */
  fromOptions?: ExtendedOption[];
  /**
   * Type of mapping override for the left side of the mapping.
   */
  fromType?: "destinationOnlyMapping";
  /**
  @default Name of model
  */
  fromLabel?: string;
  /**
  @default Icon of source
  */
  fromIcon?: string;
  fromLoadingOptions?: boolean;
  fromReloadOptions?: () => void;
  creatable?: boolean;
  /** `creatableTypes` are the types the user can select from when creating their own field. */
  creatableTypes?: Option[];
  loading?: boolean;
  object?: string;
  error?: string;
  reload?: () => void;
  advanced?: boolean;
  mappingTypes?: MappingType[];
  templates?: { name: string; description: string; placeholders?: string[] }[];
  isDisabled?: boolean;
  isPrimaryIdentityMapping?: boolean;
};

const retrieveErrorMessage = (errors: any, name: string): string => {
  for (const direction of ["from", "to"]) {
    const mappingPath = `${name}.${direction}`;
    const errorMessage = errors?.[mappingPath];
    if (typeof errorMessage === "string") {
      return errorMessage.replace(mappingPath, "This");
    }
  }
  return "";
};

/**
 * XXX: This is for making the options backward compatible
 *      since the new hightouchui needs the options to be a grouped options only.
 */
const toGroupedOption = (
  options: (ColumnOption | ExtendedOption)[],
  ungroupedOptionsLabel: string,
): { label: string; options: (ColumnOption | ExtendedOption)[] }[] => {
  const groupedOptions: {
    label: string;
    options: (ColumnOption | ExtendedOption)[];
  }[] = [];
  const unGroupedOptions: (ColumnOption | ExtendedOption)[] = [];

  for (const option of options) {
    if (!isExtendedOption(option))
      groupedOptions.push({
        label: option.label,
        options: option.options || [],
      });
    else unGroupedOptions.push(option);
  }

  if (unGroupedOptions.length)
    groupedOptions.push({
      label: ungroupedOptionsLabel,
      options: unGroupedOptions,
    });

  return groupedOptions;
};

export const Mapping: FC<Readonly<Props>> = ({
  name,
  options,
  fromType,
  fromOptions,
  fromLabel,
  fromIcon,
  fromLoadingOptions,
  fromReloadOptions,
  creatable,
  creatableTypes,
  loading,
  reload,
  error,
  advanced = false,
  mappingTypes,
  templates,
  isDisabled = false,
  isPrimaryIdentityMapping = false,
}) => {
  const { columns: formkitColumns, model } = useFormkitContext();
  const { loadingModel } = useFormkitDataContext();
  const columns =
    Array.isArray(fromOptions) && fromOptions.length > 0
      ? fromOptions
      : (formkitColumns ?? []);

  const { destinationDefinition, errors, sourceDefinition } =
    useDestinationForm();
  const { watch, setValue } = useFormContext();

  const isEventModel = Boolean(model?.isEventModel);

  const flatColumnOptions = useMemo(
    () =>
      columns.flatMap((col) =>
        "options" in col && Array.isArray(col.options)
          ? col.options.map((o) => ({
              ...o,
              description: o.description ?? undefined,
              type: o.type ?? undefined,
            }))
          : [
              {
                ...col,
                description: col.description ?? undefined,
                type: col.type ?? undefined,
              },
            ],
      ),
    [columns],
  );

  const [jsonColumnProperties, setJsonColumnProperties] =
    useState<JsonColumnProps>({
      selectedColumnProps: undefined,
      allColumnsProps: undefined,
    });

  const value = watch(name);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (!value) {
      setValue(name, {});
    }
  }, [value]);

  const permission = usePermissionContext();

  const getIsValid = ({
    errors,
    mappingDirection,
    mappingName,
  }: GetIsValidProps): boolean => {
    const error = errors?.[`${mappingName}.${mappingDirection}`];
    return Boolean(error);
  };

  if (!value) {
    return null;
  }

  return (
    <Column aria-label={`${name} mapping`} gap={3}>
      <Box
        alignItems="center"
        display="grid"
        columnGap={MAPPING_COLUMN_GAP}
        gridTemplateColumns={MAPPING_ROW_COLUMNS}
      >
        <MappingsHeader
          fromIcon={fromIcon}
          fromLabel={fromLabel}
          fromLoadingOptions={fromLoadingOptions}
          fromReloadOptions={fromReloadOptions}
          loading={loading}
          reload={reload}
          usingCustomFromOptions={Boolean(fromOptions)}
        />
      </Box>
      <Box>
        <Controller
          name={name}
          render={(data) => {
            const destinationOptions = [...(options || [])];

            const isDestinationOptionExists = destinationOptions.some(
              (option) => option.value === data.field.value?.to,
            );

            // If mapping destination is selected, but option doesn't exist, this value was entered by the user
            const isCustomDestinationOption = Boolean(
              data.field.value?.to && !isDestinationOptionExists,
            );

            if (isCustomDestinationOption) {
              destinationOptions.push({
                label: data.field.value?.to,
                value: data.field.value?.to,
              });
            }

            return (
              <Box
                alignItems="center"
                display="grid"
                columnGap={MAPPING_COLUMN_GAP}
                gridTemplateColumns={MAPPING_ROW_COLUMNS}
              >
                {advanced ? (
                  <Mapper
                    isDisabled={isDisabled || permission.unauthorized}
                    isError={getIsValid({
                      errors,
                      mappingDirection: "from",
                      mappingName: data.field.name,
                    })}
                    jsonColumnProperties={jsonColumnProperties}
                    mappingTypes={mappingTypes}
                    onChangeJsonColumnProperties={setJsonColumnProperties}
                    onReloadEligibleInlineMapperColumns={() => {}}
                    placeholder={`Select a field from ${sourceDefinition?.name ?? "the source"}`}
                    selectedOption={undefined}
                    templates={templates ?? []}
                    value={data.field.value || {}}
                    onChange={(value) => {
                      // For destinations like Iterable where fields can be created dynamically
                      // but no predefined options exist, auto-map the source field name to destination
                      if (
                        !data.field.value?.to &&
                        value.type === MappingType.STANDARD &&
                        creatable &&
                        !destinationOptions?.length
                      ) {
                        const fieldName =
                          value.alias ?? resolveFromValue(value.from);
                        data.field.onChange({
                          from: value.from,
                          to: fieldName,
                          type: value.type,
                        });
                        return;
                      }

                      if (
                        !data.field.value?.to &&
                        destinationOptions?.length &&
                        value.type === MappingType.STANDARD
                      ) {
                        const fieldName =
                          value.alias ?? resolveFromValue(value.from);
                        data.field.onChange(
                          suggest(
                            { label: fieldName, value: value.from },
                            destinationOptions,
                          ),
                        );
                      } else {
                        data.field.onChange({
                          to: data.field.value?.to,
                          object: data.field.value?.object,
                          ...value,
                        });
                      }
                    }}
                  />
                ) : columns.length > 0 || model ? (
                  <HiddenFormLabel
                    label={`Select a field from ${sourceDefinition?.name ?? "the source"}`}
                  >
                    {isEventModel ? (
                      <Combobox
                        emptyOptionsMessage="Type in a field name and Hightouch will use it as an expected column"
                        isDisabled={isDisabled || permission.unauthorized}
                        isInvalid={getIsValid({
                          errors,
                          mappingDirection: "from",
                          mappingName: data.field.name,
                        })}
                        options={getExpectedColumnOptions(
                          flatColumnOptions,
                          data.field.value?.from,
                        )}
                        placeholder={
                          data.field.value?.from
                            ? ""
                            : flatColumnOptions.length
                              ? `Select or enter a field from ${sourceDefinition?.name}`
                              : `Enter an expected column…`
                        }
                        isClearable={true}
                        supportsCreatableOptions
                        createOptionMessage={(value) =>
                          `Select "${value}" as an expected column value…`
                        }
                        isLoading={loadingModel}
                        value={data.field.value?.from}
                        width="100%"
                        onChange={(value) => {
                          data.field.onChange({
                            ...data.field.value,
                            from: value || undefined,
                            ...(fromType && { type: fromType }),
                          });
                        }}
                        onCreateOption={(value) => {
                          data.field.onChange({
                            ...data.field.value,
                            from: value || undefined,
                            ...(fromType && { type: fromType }),
                          });
                        }}
                      />
                    ) : (
                      <GroupedCombobox
                        isDisabled={isDisabled || permission.unauthorized}
                        isInvalid={getIsValid({
                          errors,
                          mappingDirection: "from",
                          mappingName: data.field.name,
                        })}
                        optionAccessories={(option) => {
                          const leftAccessory = (():
                            | IconAccessory
                            | undefined => {
                            if (
                              option?.value?.["type"] ===
                              "additionalColumnReference"
                            ) {
                              return {
                                icon: TraitIcon,
                                type: "icon",
                              };
                            } else if (option?.value?.["type"] === "boosted") {
                              return {
                                icon: SparkleIcon,
                                type: "icon",
                              };
                            } else if (
                              option?.["customType"] ||
                              option?.["type"]
                            ) {
                              return {
                                icon: NEW_ICON_MAP[
                                  convertType(
                                    option?.["customType"] || option?.["type"],
                                  )
                                ],
                                type: "icon",
                              };
                            }
                            return undefined;
                          })();
                          return leftAccessory ? { leftAccessory } : undefined;
                        }}
                        optionDescription={(option) => {
                          if (!option) return "";

                          if ("entity" in option) {
                            return option.entity?.name ?? "";
                          }

                          if ("modelName" in option) {
                            return option.modelName ?? "";
                          }

                          return "";
                        }}
                        optionGroups={toGroupedOption(
                          columns,
                          fromLabel ?? "Columns",
                        )}
                        optionLabel={(option) => {
                          return isExtendedOption(option) &&
                            option.object?.label
                            ? `${option.label} (${option.object.label})`
                            : option.label;
                        }}
                        placeholder={
                          data.field.value?.from
                            ? ""
                            : `Select a field from ${sourceDefinition?.name ?? "the source"}`
                        }
                        value={
                          resolveSourceColumn(data.field.value?.from) ??
                          data.field.value?.from
                        }
                        width="100%"
                        onChange={(value) => {
                          if (fromType) {
                            data.field.onChange({
                              ...data.field.value,
                              from: value || undefined,
                              type: fromType,
                            });
                          } else {
                            data.field.onChange({
                              ...data.field.value,
                              from: value || undefined,
                            });
                          }
                        }}
                      />
                    )}
                  </HiddenFormLabel>
                ) : (
                  <HiddenFormLabel
                    label={`Enter a field from ${sourceDefinition?.name}`}
                  >
                    <TextInput
                      isDisabled={isDisabled || permission.unauthorized}
                      placeholder={
                        data.field.value?.from
                          ? ""
                          : `Enter a field from ${sourceDefinition?.name}`
                      }
                      value={data.field.value?.from}
                      width="100%"
                      onChange={({ target: { value } }) => {
                        if (fromType) {
                          data.field.onChange({
                            ...data.field.value,
                            from: value || undefined,
                            type: fromType,
                          });
                        } else {
                          data.field.onChange({
                            ...data.field.value,
                            from: value || undefined,
                          });
                        }
                      }}
                    />
                  </HiddenFormLabel>
                )}

                <Column gap={2} fontSize="xl" color="text.tertiary">
                  <ArrowRightIcon />
                </Column>

                <Row alignItems="center" gap={2}>
                  <Column flex={1} gap={2} minWidth={0} position="relative">
                    {creatable ? (
                      <>
                        <HiddenFormLabel
                          label={`Select or enter a field from ${destinationDefinition?.name}`}
                        >
                          <Combobox
                            emptyOptionsMessage="Type in a field name and Hightouch will create that field in the destination"
                            isClearable
                            isDisabled={isDisabled || permission.unauthorized}
                            isInvalid={getIsValid({
                              errors,
                              mappingDirection: "to",
                              mappingName: data.field.name,
                            })}
                            optionAccessories={(option) => {
                              return option?.extendedType?.type
                                ? {
                                    leftAccessory: {
                                      type: "icon",
                                      icon: NEW_ICON_MAP[
                                        option.extendedType.type
                                      ],
                                    },
                                  }
                                : undefined;
                            }}
                            optionLabel={(option) => {
                              return option.object?.label
                                ? `${option.label} (${option.object.label})`
                                : option.label;
                            }}
                            options={destinationOptions}
                            placeholder={
                              data.field.value?.to
                                ? ""
                                : `Select or enter a field from ${destinationDefinition?.name}`
                            }
                            supportsCreatableOptions
                            value={data.field.value?.to}
                            width="100%"
                            onChange={(value) => {
                              const option = (options || []).find(
                                (option) => option.value === value,
                              );

                              data.field.onChange({
                                ...data.field.value,
                                fieldType:
                                  option?.extendedType?.type || undefined,
                                to: value || undefined,
                              });
                            }}
                            onCreateOption={(inputValue) => {
                              data.field.onChange({
                                ...data.field.value,
                                to: inputValue || undefined,
                              });
                            }}
                          />
                        </HiddenFormLabel>

                        {creatableTypes && (
                          <TypeSelect
                            isDisabled={isDisabled || permission.unauthorized}
                            options={creatableTypes}
                            placeholder="Select a field type"
                            value={data.field.value}
                            created={!isCustomDestinationOption}
                            width="100%"
                            onTypeCreate={(value, typeOptions) => {
                              data.field.onChange({
                                ...data.field.value,
                                fieldType: value,
                                typeOptions,
                              });
                            }}
                          />
                        )}
                      </>
                    ) : (
                      <HiddenFormLabel
                        label={`Select a field from ${destinationDefinition?.name}`}
                      >
                        <Combobox
                          isDisabled={isDisabled || permission.unauthorized}
                          isInvalid={getIsValid({
                            errors,
                            mappingDirection: "to",
                            mappingName: data.field.name,
                          })}
                          optionAccessories={(option) => {
                            return option?.extendedType?.type
                              ? {
                                  leftAccessory: {
                                    type: "icon",
                                    icon: NEW_ICON_MAP[
                                      option.extendedType.type
                                    ],
                                  },
                                }
                              : undefined;
                          }}
                          optionLabel={(option) => {
                            return option.object?.label
                              ? `${option.label} (${option.object.label})`
                              : option.label;
                          }}
                          options={destinationOptions}
                          placeholder={
                            data.field.value?.to
                              ? ""
                              : `Select a field from ${destinationDefinition?.name}`
                          }
                          value={data.field.value?.to}
                          width="100%"
                          onChange={(value) => {
                            data.field.onChange({
                              ...data.field.value,
                              to: value || undefined,
                            });
                          }}
                        />
                      </HiddenFormLabel>
                    )}
                  </Column>
                  {(retrieveErrorMessage(errors, name) || error) && (
                    <Tooltip
                      message={
                        retrieveErrorMessage(errors, name) || error || ""
                      }
                    >
                      <Row fontSize="24px">
                        <ErrorIcon color="danger.base" />
                      </Row>
                    </Tooltip>
                  )}
                </Row>
              </Box>
            );
          }}
        />
      </Box>
      {isPrimaryIdentityMapping && (
        <ArrayExpansionSection
          flatColumns={flatColumnOptions}
          mappingName={name}
        />
      )}
    </Column>
  );
};
